import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface Client {
  id: string;
  name: string;
  code: string;
  warehouseSystem: string;
  databaseDialect: string;
  isActive: boolean;
  location: string;
  industry: string;
  logoUrl?: string;
  logoBase64?: string;
}

interface ClientContextType {
  selectedClient: Client | null;
  setSelectedClient: (client: Client | null) => void;
  clearSelectedClient: () => void;
}

const ClientContext = createContext<ClientContextType | undefined>(undefined);

interface ClientProviderProps {
  children: ReactNode;
}

export const ClientProvider: React.FC<ClientProviderProps> = ({ children }) => {
  const [selectedClient, setSelectedClientState] = useState<Client | null>(null);

  // Load client from sessionStorage on mount
  useEffect(() => {
    const storedClient = sessionStorage.getItem('selectedClient');
    if (storedClient) {
      try {
        const client = JSON.parse(storedClient);
        setSelectedClientState(client);
        console.log('Loaded client from session:', client.name);
      } catch (error) {
        console.error('Error parsing stored client:', error);
        sessionStorage.removeItem('selectedClient');
      }
    }
  }, []);

  const setSelectedClient = (client: Client | null) => {
    setSelectedClientState(client);

    if (client) {
      // Store in sessionStorage
      sessionStorage.setItem('selectedClient', JSON.stringify(client));
      console.log('Client selected and stored in session:', client.name);
    } else {
      // Remove from sessionStorage
      sessionStorage.removeItem('selectedClient');
      console.log('Client cleared from session');
    }
  };

  const clearSelectedClient = () => {
    setSelectedClient(null);
  };

  const value: ClientContextType = {
    selectedClient,
    setSelectedClient,
    clearSelectedClient,
  };

  return (
    <ClientContext.Provider value={value}>
      {children}
    </ClientContext.Provider>
  );
};

export const useClient = (): ClientContextType => {
  const context = useContext(ClientContext);
  if (context === undefined) {
    throw new Error('useClient must be used within a ClientProvider');
  }
  return context;
};

// Hook to get current client code for URL routing
export const useClientCode = (): string | null => {
  const { selectedClient } = useClient();
  return selectedClient?.code || null;
};

// Hook to check if a specific client is selected
export const useIsClientSelected = (clientCode: string): boolean => {
  const { selectedClient } = useClient();
  return selectedClient?.code === clientCode;
};